import { FeatureFlagsRulesServiceBase, HttpService, LocalContext, ObservableCollection, QueryBus, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { AddressModel, DocumentPaymentListItemModel, ExchangeRateModel, RelatedEntity, SaleDocumentItemModel, SaleDocumentModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO, VatTableItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, documentDeliveryAddressFilled, serializeAddressModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentItemModel, serializeDocumentPaymentListItemModel } from '../common/serialziation-utils';
import { ProductJpkVatGroupCollectionQuery } from '../product/queries/product-jpk-vat-group.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { SaleJpkVatGroupCollectionQuery } from './queries/sale-jpk-vat-group.query-base';
import { SaleDocumentBO } from './sale-document.bo';

@Repository({
    boType: SaleDocumentBO,
    apiController: TaggedUrl.join('sales', [Tags.Api]),
    dto: {
        get: SaleGetDocumentDTO,
        update: SaleUpdateDocumentDTO
    }
})
export class SaleDocumentRepositoryBase extends RepositoryBase<SaleDocumentBO, SaleDocumentModel, SaleGetDocumentDTO, SaleUpdateDocumentDTO> {
    public constructor(
        http: HttpService,
        private readonly weaver: Weaver,
        private readonly productRepository: ProductRepositoryContract,
        private readonly clientRepository: ClientRepositoryBase,
        private readonly queryBus: QueryBus,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(http);
    }

    protected createBO(): SaleDocumentBO {
        return new SaleDocumentBO(this.weaver, this.productRepository, this.clientRepository, this.featureFlagsRulesService, this.queryBus);
    }

    public async fromPlainObjectAsync(item: SaleGetDocumentDTO): Promise<SaleDocumentModel> {
        const document = new SaleDocumentModel(pickBy<Partial<SaleDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,

            relatedInventoryDocuments: item.relatedInventoryDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            includesInventoryControl: item.includesInventoryControl,
            inventoryEffect: item.inventoryEffect,

            ddkState: item.ddkState,
            eppState: item.eppState,
            fiscalizationState: item.fiscalizationState,
            jpk_VATState: item.jpk_vatState,
            orderState: item.orderState,

            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            communityTransactionId: item.communityTransactionId,
            currencyId: item.currencyId,
            orderExternalId: item.orderExternalId,
            documentNumber: item.documentNumber,
            dueDate: item.dueDate,
            dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
            favourite: item.favourite,
            grossValue: item.totalGrossValue,

            issueDate: item.issueDate,
            goodsIssueDate: item.goodsIssueDate || ((item.inventoryEffect && item.deliveryDate) || null),
            issuedByVatPayer: item.issuedByVatPayer,
            issuer: item.issuer,
            kind: item.kind,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            invoiceToReceipt: item.invoiceToReceipt,
            marginSchemeId: item.marginSchemeId,
            netValue: item.totalNetValue,
            note: item.note,
            numberingId: item.numberingId,
            paymentMethodId: item.paymentMethodId,
            recipient: item.recipient,
            saleDate: item.deliveryDate,
            splitPayment: item.splitPayment,
            taxLiabilityDate: item.taxLiabilityDate,
            transactionLocation: item.transactionLocation,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            customDueDateDescription: item.customDueDateDescription,
            ksefSubmissionMode: item.ksefSubmissionMode,
            ksefProcessingStage: item.ksefProcessingStage,
            ksefDocumentStatus: item.ksefDocumentStatus,
            ksefDocumentId: item.ksefDocumentId,
            ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
            ksefAcquisitionDateTime: item.ksefAcquisitionDateTime,
            ksefLastStatusCheckDate: item.ksefLastStatusCheckDate,
            ksefIssueDate: item.ksefIssueDate,
            ksefSessionReferenceNumber: item.ksefSessionReferenceNumber,
            ksefLastReceivingUpoDate: item.ksefLastReceivingUpoDate,
            ksefSessionState: item.ksefSessionState,
            ksefSessionEndDate: item.ksefSessionEndDate,
            ksefAllDocumentsProcessed: item.ksefAllDocumentsProcessed,
            ksefInitializationDate: item.ksefInitializationDate,
            ksefHash: item.ksefHash,
            ksefUpoDownloadUrl: item.ksefUpoDownloadUrl,
            ksefUpoDownloadUrlExpirationDate: item.ksefUpoDownloadUrlExpirationDate,
            ksefCertificateUrl: item.ksefCertificateUrl,
            ksefXmlExported: item.ksefXmlExported,
            ksefXmlImported: item.ksefXmlImported,
            ksefXmlUrl: item.ksefXmlUrl,
            orderNumber: item.additionalData?.orderNumber,
            orderDate: item.additionalData?.orderDate,
            agreementNumber: item.additionalData?.agreementNumber,
            agreementDate: item.additionalData?.agreementDate,
            reverseCharge: item.reverseCharge,
            rateNP: item.rateNP
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            line1: item.deliveryAddress.address,
            name: item.deliveryAddress.name,
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId,
            zipCode: item.deliveryAddress.zipCode
        }, val => val !== void 0)) : null as any;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleDocumentItemModel(pickBy<Partial<SaleDocumentItemModel>>({
                cost: documentItem.cost,
                ordinalNumber: documentItem.no,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                jpkVatGroups: documentItem.jpkVatGroups,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                relatedInventoryItemsIds: documentItem.relatedInventoryItemsIds || [],
                includesInventoryControl: documentItem.includesInventoryControl,
                note: documentItem.note,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                id: documentItem.id,
                price_netBeforeDiscount: documentItem.unitNetPrice,
                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                cnCode: documentItem.cnCode,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                quantity: documentItem.quantity,
                unitCost: documentItem.unitCost,
                unitId: documentItem.unitId,
                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,
                vatRateId: documentItem.vatRateId,
            }, val => val !== void 0));
        }));

        if (item.exchangeRate) {
            document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0));
        }

        document.payments = new ObservableCollection(item.payments.map(item => {
            return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                id: item.id,
                date: item.date,
                value: item.amount,
                onDocument: item.onDocument
            }, val => val !== void 0));
        }));

        document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.currencyGross ?? item.gross,
                netValue: item.currencyNet ?? item.net,
                vatRateId: item.vatRateId,
                vatValue: item.currencyVat ?? item.vat,
                cost: item.cost
            }, val => val !== void 0));
        }));

        document.vatTableItemsInBaseCurrency = item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.gross,
                netValue: item.net,
                vatRateId: item.vatRateId,
                vatValue: item.vat,
                cost: item.cost
            }, val => val !== void 0));
        })) : new ObservableCollection();

        return document;
    }

    protected async preparePlainObjectAsync(model: SaleDocumentModel, ctx: LocalContext): Promise<SaleUpdateDocumentDTO> {
        const jpkVatDocumentGroupCodes = await ctx.getAsync(SaleJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));
        const jpkVatProductGroupCodes = await ctx.getAsync(ProductJpkVatGroupCollectionQuery).then(result => result.map(g => g.code));

        const item: SaleUpdateDocumentDTO = {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            orderExternalId: model.orderExternalId,
            version: model.version,
            relatedInventoryDocuments: model.relatedInventoryDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            inventoryEffect: model.inventoryEffect,
            ksefSubmissionMode: model.ksefSubmissionMode,

            bankAccountId: model.bankAccountNumberId as any,
            baseCurrencyBankAccountId: model.baseCurrencyBankAccountNumberId as any,
            buyer: model.buyer ? serializeDocumentBuyerModel(model.buyer) : null as any,
            calculationMethod: model.calculationMethod,
            cashBasis: model.cashBasis,
            client: model.client ? serializeDocumentClientModel(model.client) : null as any,
            communityTransactionId: model.communityTransactionId,
            currencyId: model.currencyId,
            deliveryAddress: documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress) : null as any,
            deliveryDate: getStartOfDate(model.saleDate),
            documentNumber: model.documentNumber,
            dueDate: getStartOfDate(model.dueDate),
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null as any,
            favourite: model.favourite,
            issuedByVatPayer: model.issuedByVatPayer,
            issueDate: getStartOfDate(model.issueDate),
            goodsIssueDate: model.goodsIssueDate ? getStartOfDate(model.goodsIssueDate) : null,
            issuer: model.issuer,
            items: model.items.map(serializeDocumentItemModel),
            jpkVatDocumentGroups: model.jpkVatGroups.filter(g => jpkVatDocumentGroupCodes.includes(g)),
            jpkVatProductGroups: model.jpkVatGroups.filter(g => jpkVatProductGroupCodes.includes(g)),
            invoiceToReceipt: model.invoiceToReceipt,
            kind: model.kind,
            marginSchemeId: model.marginSchemeId,
            note: model.note,
            numberingId: model.numberingId,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),
            paymentMethodId: model.paymentMethodId,
            recipient: model.recipient,
            splitPayment: model.splitPayment,
            taxLiabilityDate: getStartOfDate(model.taxLiabilityDate),
            transactionLocation: model.transactionLocation,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            customDueDateDescription: model.customDueDateDescription,
            vatReleaseReasonId: model.vatReleaseReasonId,
            vatReleaseReasonCustomName: model.vatReleaseReasonCustomName as any,
            vatReleaseReasonSource: model.vatReleaseReasonSource,
            additionalData: {
                orderNumber: model.orderNumber,
                orderDate: model.orderDate ? getStartOfDate(model.orderDate) : null,
                agreementNumber: model.agreementNumber,
                agreementDate: model.agreementDate ? getStartOfDate(model.agreementDate) : null
            },
            reverseCharge: model.reverseCharge,
            rateNP: model.rateNP,
            adjustPaymentDate: false
        };
        if (model.implementedDocumentId) {
            item.implementedDocumentId = model.implementedDocumentId;
            item.implementedDocumentVersion = model.implementedDocumentVersion;
        }
        if (model.id && model.documentNumber) {
            item.documentNumber = model.documentNumber;
        }
        return item;
    }
}
